import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Star, ShieldCheck, Clock, Scissors, Car, PawPrint, WheelchairMotion, Info, BookmarkSimple, Heart, Lightning } from "@phosphor-icons/react";
import ImgFallback from "@/components/ImgFallback";
import { useAuth } from "@/context/AuthContext";

function TrustRow({ studio }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-y border-cream/10 py-5 my-6" data-testid="trust-row">
      <div className="text-center">
        <p className="font-display text-2xl text-cream">{studio.completed_cuts}+</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">Verified cuts</p>
      </div>
      <div className="text-center border-x border-cream/10">
        <p className="font-display text-2xl text-gold flex items-center justify-center gap-1"><Star size={18} weight="fill" /> {studio.rating}</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">Avg rating</p>
      </div>
      <div className="text-center">
        <p className="font-display text-2xl text-cream flex items-center justify-center gap-1"><ShieldCheck size={20} weight="fill" color="#C5A059" /></p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">ID verified</p>
      </div>
    </div>
  );
}

function ReviewBar({ label, value }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs uppercase tracking-[0.2em] text-cream/60 w-24">{label}</span>
      <div className="flex-1 h-1 bg-cream/10">
        <div className="h-1 bg-gold" style={{ width: `${(value / 5) * 100}%` }} />
      </div>
      <span className="text-xs text-cream/70 w-8 text-right">{value.toFixed(1)}</span>
    </div>
  );
}

function formatSlot(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function StudioPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [studio, setStudio] = useState(null);
  const [activeService, setActiveService] = useState(null);
  const [saved, setSaved] = useState(false);
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    api.get(`/studios/${id}`).then((r) => {
      setStudio(r.data);
      setActiveService(r.data.services?.[0]?.id);
    });
  }, [id]);

  const toggleSave = async () => {
    if (!user) { toast.error("Sign in to save"); return; }
    try {
      await api.post(`/saved/${id}`);
      setSaved(!saved);
      toast.success(saved ? "Studio unsaved" : "Studio saved");
    } catch { toast.error("Save failed"); }
  };
  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow"); return; }
    try {
      await api.post(`/follow/${id}`);
      setFollowing(!following);
      toast.success(following ? "Unfollowed" : "Following");
    } catch { toast.error("Follow failed"); }
  };

  if (!studio) return <div className="max-w-6xl mx-auto px-5 py-10 text-cream/50" data-testid="studio-loading">Loading…</div>;

  const reviews = studio.reviews || [];
  const avgResult = reviews.length ? reviews.reduce((a, r) => a + r.result, 0) / reviews.length : studio.rating;
  const avgExperience = reviews.length ? reviews.reduce((a, r) => a + r.experience, 0) / reviews.length : studio.rating;
  const avgStudioR = reviews.length ? reviews.reduce((a, r) => a + r.studio_rating, 0) / reviews.length : studio.rating;
  const nextOpenings = (studio.availability || []).slice(0, 4);

  return (
    <div className="pb-32" data-testid="studio-page">
      {/* Immersive hero — hair-first via hero_look, fallback to environment */}
      <div className="relative">
        <ImgFallback src={studio.hero_look || studio.cover_image} alt={`${studio.name} — Studio`} className="w-full h-[60vh] md:h-[70vh] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
        <div className="absolute bottom-6 left-5 right-5 max-w-6xl mx-auto">
          <p className="overline mb-2">Studio</p>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter text-cream">{studio.name}</h1>
          <p className="mt-2 text-cream/80">By {studio.professional_name}</p>
          <p className="text-xs text-cream/60 mt-1 flex items-center flex-wrap gap-1">
            <MapPin size={12} weight="fill" color="#C5A059" /> Approx. {studio.area}, {studio.city} · {studio.distance_km} km
            <span className="divider-dot" /> exact address released 90 min before appointment
          </p>
          {studio.tonight_slot && (
            <p className="text-xs text-gold mt-2 flex items-center gap-1">
              <Clock size={12} weight="fill" /> Next tonight · {studio.tonight_slot}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 md:grid md:grid-cols-3 md:gap-10">
        <div className="md:col-span-2">
          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
            <div className="flex gap-2">
              <button onClick={toggleSave} data-testid="studio-save" className="btn-ghost px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                <BookmarkSimple size={14} weight={saved ? "fill" : "regular"} color={saved ? "#C5A059" : "#FAF9F6"} /> {saved ? "Saved" : "Save"}
              </button>
              <button onClick={toggleFollow} data-testid="studio-follow" className="btn-ghost px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                <Heart size={14} weight={following ? "fill" : "regular"} color={following ? "#C5A059" : "#FAF9F6"} /> {following ? "Following" : "Follow Studio"}
              </button>
            </div>
            {studio.instant_book && (
              <span className="chip bg-gold/90 text-ink border-transparent font-semibold text-[10px]">
                <Lightning size={10} weight="fill" /> Instant Book
              </span>
            )}
          </div>

          <TrustRow studio={studio} />

          {/* Portfolio */}
          <section className="mt-10">
            <p className="overline mb-4">Portfolio</p>
            <div className="grid grid-cols-3 gap-2" data-testid="portfolio-grid">
              {studio.portfolio.map((img, i) => (
                <Link
                  key={i} to={`/look/${studio.id}-${i}`}
                  className="block relative overflow-hidden group aspect-square"
                  data-testid={`portfolio-item-${i}`}
                >
                  <ImgFallback src={img} alt={`${studio.name} portfolio ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </Link>
              ))}
            </div>
          </section>

          {/* Availability */}
          <section className="mt-14" data-testid="availability-section">
            <p className="overline mb-4">Next available</p>
            <div className="flex flex-wrap gap-2">
              {nextOpenings.map((iso, i) => (
                <button
                  key={iso}
                  data-testid={`slot-${i}`}
                  className="chip hover:border-gold hover:text-gold transition-colors"
                >
                  {formatSlot(iso)}
                </button>
              ))}
              {nextOpenings.length === 0 && <p className="text-cream/50 text-sm">No published openings — try Request to Book.</p>}
            </div>
            <div className="mt-4">
              <Link
                to={`/after-hours/new/${studio.id}`}
                data-testid="studio-after-hours"
                className="inline-flex items-center gap-2 btn-ghost px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] hover:border-gold hover:text-gold"
              >
                Request a custom time (after-hours)
              </Link>
            </div>
          </section>

          {/* Services with per-service book */}
          <section className="mt-14">
            <p className="overline mb-4">Services</p>
            <div className="space-y-3">
              {studio.services.map((sv) => (
                <div
                  key={sv.id}
                  data-testid={`service-${sv.id}`}
                  className={`p-5 border transition-colors ${activeService === sv.id ? "border-gold bg-cream/[0.02]" : "border-cream/10"}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <button
                      onClick={() => setActiveService(sv.id)}
                      className="text-left flex-1"
                    >
                      <p className="font-display text-xl tracking-tight text-cream flex items-center gap-2">
                        <Scissors size={16} color="#C5A059" /> {sv.name}
                      </p>
                      <p className="text-xs text-cream/60 mt-1 flex items-center gap-2">
                        <Clock size={12} /> {sv.duration_minutes} min
                      </p>
                      <p className="text-sm text-cream/70 mt-2 leading-relaxed max-w-lg">{sv.description}</p>
                    </button>
                    <div className="text-right whitespace-nowrap">
                      <p className="font-display text-2xl text-gold">${sv.price}</p>
                      <button
                        data-testid={`service-book-${sv.id}`}
                        onClick={() => nav(`/booking/new/${studio.id}?service=${sv.id}`)}
                        className="mt-2 btn-gold px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold"
                      >
                        Book
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* About */}
          <section className="mt-14">
            <p className="overline mb-4">About the Professional</p>
            <p className="text-cream/85 leading-relaxed max-w-2xl">{studio.about}</p>
          </section>

          {/* Studio info */}
          <section className="mt-14">
            <p className="overline mb-4">Studio information</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: Car, label: "Street parking" },
                { icon: PawPrint, label: "No pets on site" },
                { icon: WheelchairMotion, label: "Ground floor access" },
                { icon: Info, label: "One chair, private" },
              ].map((x, i) => (
                <div key={i} className="border border-cream/10 p-4 flex items-center gap-3">
                  <x.icon size={18} color="#C5A059" />
                  <span className="text-xs uppercase tracking-[0.15em] text-cream/70">{x.label}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Reviews */}
          <section className="mt-14" data-testid="reviews-section">
            <p className="overline mb-4">Reviews</p>
            <div className="border border-cream/10 p-6 space-y-3">
              <ReviewBar label="Overall" value={studio.rating} />
              <ReviewBar label="Result" value={avgResult} />
              <ReviewBar label="Experience" value={avgExperience} />
              <ReviewBar label="Studio" value={avgStudioR} />
            </div>
            <div className="mt-4 space-y-4">
              {reviews.length === 0 && (
                <p className="text-cream/50 text-sm italic">Only Members who complete a verified booking can leave reviews.</p>
              )}
              {reviews.map((r) => (
                <div key={r.id} className="border-b border-cream/10 pb-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gold mb-1 flex items-center gap-1">
                    <ShieldCheck size={12} weight="fill" /> Verified booking
                  </p>
                  <p className="text-sm text-cream">{r.comment}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-2">{r.member_name} · {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sticky sidebar (desktop) */}
        <aside className="hidden md:block relative mt-6">
          <div className="sticky top-24 border border-cream/10 p-6">
            <p className="overline mb-2">Ready when you are</p>
            <p className="font-display text-2xl tracking-tight mb-1">from ${studio.starting_price}</p>
            <p className="text-xs text-cream/60 mb-6">No additional KUTZ Member booking fee.</p>
            <button
              data-testid="book-now-desktop"
              onClick={() => nav(`/booking/new/${studio.id}${activeService ? `?service=${activeService}` : ""}`)}
              className="btn-gold w-full px-6 py-4 rounded-full text-xs uppercase tracking-[0.25em] font-semibold"
            >
              Book now
            </button>
            <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40 mt-4 text-center">Instant Book · confirmed in seconds</p>
          </div>
        </aside>
      </div>

      {/* Sticky mobile Book Now */}
      <div className="md:hidden fixed bottom-16 inset-x-0 z-40 px-4 pb-3 bg-gradient-to-t from-ink to-transparent pt-6">
        <button
          data-testid="book-now-mobile"
          onClick={() => nav(`/booking/new/${studio.id}${activeService ? `?service=${activeService}` : ""}`)}
          className="btn-gold w-full px-6 py-4 rounded-full text-xs uppercase tracking-[0.25em] font-semibold"
        >
          Book now · from ${studio.starting_price}
        </button>
      </div>
    </div>
  );
}
